import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Controller,
  FieldValues,
  FieldPath,
  UseControllerProps,
  UseFormReturn,
  useFormState,
} from 'react-hook-form';
import FormField from '@ingka/form-field';
import TextArea from '@ingka/text-area';

import { getter } from '@/lib/utils/object';
import { extractErrorMessage } from '../error-message';

export type ControlledTextAreaProps<T extends FieldValues> = {
  className?: string;
  // Form
  name: FieldPath<T>;
  rules?: UseControllerProps<T>['rules'];
  forcedError?: any;
  label: string;
  readOnly?: boolean;
  shouldValidate?: boolean;
  successMessage?: string;
  onChange?: () => void;
};

export function ControlledTextArea<T extends FieldValues>(
  props: PropsWithChildren<ControlledTextAreaProps<T>>
) {
  const { t } = useTranslation();
  const {
    name,
    rules,
    shouldValidate = true,
    label,
    readOnly,
    successMessage,
    onChange,
    forcedError,
  } = props;
  // Error handling
  const { errors } = useFormState({ name });
  const error = forcedError || getter(errors, name);
  const errorMsgArgs = error && extractErrorMessage(error);
  const errorMsg = error && t(...errorMsgArgs);

  return (
    <div className={props.className}>
      <Controller
        name={name}
        rules={rules}
        render={({ field }) => (
          <FormField
            fieldHelper={{
              msg: shouldValidate ? successMessage : '',
              type: 'error',
            }}
            validation={{
              msg: errorMsg,
              type: 'error',
            }}
            valid={!error}
            shouldValidate={!!shouldValidate}
          >
            <TextArea
              {...field}
              data-testid={`${name}TextArea`}
              onChange={(e) => {
                field.onChange(e);
                onChange?.();
              }}
              id={name}
              label={label}
              readOnly={readOnly}
              value={field.value || ''}
            />
          </FormField>
        )}
      />
    </div>
  );
}

export default ControlledTextArea;
